import React, { useState, useContext } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { ExamsQuestionsModalSubmitted } from './exams-questions-modal-submitted';
import { QuestionBooksContext } from '@/providers/QuestionBooksProvider';
import { useTranslation } from 'react-i18next';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { Sheet, SheetBody, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { CardKpi, CardKpiGrid } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import tiaPensando from '@/assets/images/tia/TIA-PENSANDO.png';
import tiaJoinha from '@/assets/images/tia/TIA-JOINHA.png';

export function ExamsQuestionsModalEnd(props) {
  const { questionBooksUser, show, onClose, questionBook, questions, countAnswered, countCorrectAnswers, finishQuestionBook, elapsedTime, formattedDate } = props;
  const { t } = useTranslation();
  const client = useClient();
  const playfulStudent = usePlayfulStudent();
  const [showModal, setShowModal] = useState(false);
  const { setCorrectAnswers } = useContext(QuestionBooksContext);

  const handleFinalizeExam = async () => {
    const { error } = await client.mutate(['question_books_users', questionBooksUser?.id], { done: true });
    if (error) {
      toast.error(response.error.title);
    } else {
      setShowModal(true);
      onClose();
    }
  };

  const handleSaveAnswers = async () => {
    setCorrectAnswers(countCorrectAnswers());
  };

  function convertToMinutes(elapsedTime) {
    return Math.floor(elapsedTime / 60);
  }

  const elapsedTimeInMinutes = Math.floor(elapsedTime / 60);

  // Confirmação lúdica: modal centrado com a T.I.A no contexto — joinha se
  // respondeu tudo, pensativa se ainda há questões em branco.
  if (playfulStudent) {
    const answered = countAnswered();
    const total = questions.length;
    const missing = total - answered;
    const allAnswered = missing <= 0;

    return (
      <>
        <Dialog
          open={show}
          onOpenChange={onClose}
        >
          <DialogContent
            size="sm"
            hideCloseButton
            className="w-[400px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
          >
            <div
              className="flex justify-center rounded-t-[24px] pt-6"
              style={{ background: 'var(--color-bg-lilac)' }}
            >
              <img
                src={allAnswered ? tiaJoinha : tiaPensando}
                alt=""
                aria-hidden="true"
                className="h-32 w-auto object-contain object-bottom"
              />
            </div>

            <div className="flex flex-col items-center px-6 pb-6 pt-5 text-center">
              <DialogTitle
                className="mb-2 text-lg font-extrabold"
                style={{ color: '#222731' }}
              >
                {allAnswered ? 'Tudo pronto pra entregar?' : missing === 1 ? 'Ainda falta 1 questão!' : `Ainda faltam ${missing} questões!`}
              </DialogTitle>
              <DialogDescription className="mb-4 text-sm font-semibold leading-relaxed text-neutral-500">
                {allAnswered ? 'Você respondeu todas as questões. Quer finalizar e enviar suas respostas?' : `Você respondeu ${answered} de ${total}. Quer entregar mesmo assim ou prefere revisar antes?`}
              </DialogDescription>

              <div className="mb-6 flex items-center gap-2 rounded-full bg-[var(--color-bg-teal-soft)] px-4 py-2 text-sm font-extrabold text-[var(--color-primary)]">
                <FiCheckCircle className="h-4 w-4" />
                {answered}/{total} respondidas
              </div>

              <button
                type="button"
                autoFocus
                onClick={handleFinalizeExam}
                className="relative w-full overflow-hidden rounded-full py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
                style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
                />
                <span className="relative">Finalizar e enviar 🚀</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
              >
                Ainda não
              </button>
            </div>
          </DialogContent>
        </Dialog>

        <ExamsQuestionsModalSubmitted
          formattedDate={formattedDate}
          elapsedTimeInMinutes={elapsedTimeInMinutes}
          countAnswered={countAnswered}
          show={showModal}
          onSave={handleSaveAnswers}
          questionBook={questionBook}
          questions={questions}
          finishQuestionBook={finishQuestionBook}
        />
      </>
    );
  }

  return (
    <>
      <Sheet
        open={show}
        onOpenChange={onClose}
      >
        <SheetContent size="md">
          <SheetBody className="pb-6 pt-12">
            <SheetTitle>{t('exams.submitExam')}</SheetTitle>

            <p className="mb-4">{t('exams.subtitleSubmitExam')}</p>

            <CardKpi className="!mb-3 !justify-start !pl-4">
              <FiCheckCircle />{' '}
              <span className="font-light">
                <strong>
                  {countAnswered()}/{questions.length}
                </strong>{' '}
                - {t('exams.submitedQuestions')}
              </span>
            </CardKpi>

            <CardKpiGrid className="max-sm:grid-cols-1">
              <Button
                variant="outline"
                wide
                onClick={onClose}
              >
                {t('button.notFinish')}
              </Button>

              <Button
                wide
                onClick={handleFinalizeExam}
              >
                {t('button.submitAndFinish')}
              </Button>
            </CardKpiGrid>
          </SheetBody>
        </SheetContent>
      </Sheet>

      <ExamsQuestionsModalSubmitted
        formattedDate={formattedDate}
        elapsedTimeInMinutes={elapsedTimeInMinutes}
        countAnswered={countAnswered}
        show={showModal}
        onSave={handleSaveAnswers}
        questionBook={questionBook}
        questions={questions}
        finishQuestionBook={finishQuestionBook}
      />
    </>
  );
}
